import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import ConversationItem from "./ConversationItem";
import { getAllConversations, listAgents } from "../services/api";
import ConversationPreview from "./ConversationPreview";
import SearchableDropdown from "./SearchableDropdown";

export default function Conversations() {
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [assistantsLoading, setAssistantsLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const selectedConversationRef = useRef(null);
  const conversationListRef = useRef(null);
  const [containerReady, setContainerReady] = useState(false);

  // Callback ref to track when the container is mounted
  const setConversationListRef = useCallback((node) => {
    conversationListRef.current = node;
    if (node) {
      setContainerReady(true);
    } else {
      setContainerReady(false);
    }
  }, []);

  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    assistantId: '',
  });
  const [isSandboxChecked, setIsSandboxChecked] = useState(false);
  const [visibleConversations, setVisibleConversations] = useState(10);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const agentOptions = useMemo(
    () => [
      { key: '— All agents —', value: '', valueDisplay: 'Clear filter' },
      ...[...assistants]
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        .map((row) => ({
          key: `${row.name || row.assistant_id}`.trim() || row.assistant_id,
          value: row.assistant_id,
          valueDisplay: row.assistant_id,
        })),
    ],
    [assistants]
  );

  const loadMoreConversations = useCallback(() => {
    if (!isLoadingMore) {
      setIsLoadingMore(true);
      setVisibleConversations(prev => prev + 10);
      setTimeout(() => setIsLoadingMore(false), 500);
    }
  }, [isLoadingMore]);

  const handleScroll = useCallback(() => {
    const container = conversationListRef.current;
    if (container) {
      const scrollPosition = container.scrollTop + container.clientHeight;
      const scrollThreshold = container.scrollHeight - 5;

      if (scrollPosition >= scrollThreshold) {
        loadMoreConversations();
      }
    }
  }, [loadMoreConversations]);

  // Effect to handle scroll event listener setup
  useEffect(() => {
    if (!conversationsLoading && containerReady) {
      const container = conversationListRef.current;
      if (container) {
        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
      }
    }
  }, [handleScroll, conversationsLoading, containerReady]);

  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const response = await getAllConversations();
        setConversations(response.data);
        setFilteredConversations(response.data);
      } catch (err) {
        setErrors(prevErrors => [...prevErrors, 'Error fetching conversations']);
      } finally {
        setConversationsLoading(false);
      }
    };

    const fetchAssistants = async () => {
      try {
        const response = await listAgents();
        const rows = Array.isArray(response.data?.data) ? response.data.data : [];
        setAssistants(rows);
      } catch (err) {
        setErrors(prevErrors => [...prevErrors, 'Error fetching agents']);
      } finally {
        setAssistantsLoading(false);
      }
    };

    fetchConversations();
    fetchAssistants();
  }, []);

  useEffect(() => {
    const isWithinDateRange = (timestamp) => {
      const conversationDate = new Date(timestamp).toISOString().split('T')[0];
      const { startDate, endDate } = filters;
      if (startDate && endDate) {
        return conversationDate >= startDate && conversationDate <= endDate;
      }
      return true;
    };

    const results = conversations.filter(conversation => {
      const matchesDate = isWithinDateRange(conversation.timestamp);
      const matchesSandbox = isSandboxChecked || (conversation.source?.url !== "no source url found" && conversation.source?.url !== "sandbox");
      const matchesAssistantId = !filters.assistantId || conversation.assistant_id === filters.assistantId;
      // exclude saaq assistant
      const isNotExcludedAssistant = conversation.assistant_id !== "asst_u9rmKaomBx0bgY1GHnSOyPWd";
      // exclude conversations with only assistant messages (no user interaction)
      const hasUserMessages = conversation.messages && conversation.messages.some(message => message.role === "user");
      // exclude conversations where all user messages start with [CHATBOT_ALERT
      const userMessages = conversation.messages?.filter(message => message.role === "user") || [];
      const isNotChatbotAlert = userMessages.length === 0 || !userMessages.every(message => message.value?.startsWith("[CHATBOT_ALERT"));

      return matchesDate && matchesSandbox && matchesAssistantId && isNotExcludedAssistant && hasUserMessages && isNotChatbotAlert;
    });

    setFilteredConversations(results);
  }, [filters, conversations, isSandboxChecked]);

  const handleSelectedConvo = (id) => {
    const conv = conversations.find((c) => c.thread_id === id);
    setSelectedConversation(conv);

    if (selectedConversationRef.current) {
      selectedConversationRef.current.scrollTop = 0;
    }
  };

  const handleDeleteConversation = (threadId) => {
    setConversations(prev => prev.filter(c => c.thread_id !== threadId));
    if (selectedConversation && selectedConversation.thread_id === threadId) {
      setSelectedConversation(null);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: value
    }));
  };

  const handleSandboxChange = (e) => {
    setIsSandboxChecked(e.target.checked);
  };

  if (conversationsLoading || assistantsLoading) return <div className="loading">Loading...</div>;
  if (errors.length > 0) return <div className="error">{errors.join('. ')}</div>;

  const sortedConversations = [...filteredConversations].sort((a, b) => {
    return new Date(b.timestamp) - new Date(a.timestamp);
  });

  return (
    <div className="conversations" style={{ paddingTop: '80px' }}>
      <div className="conversation-container">
        <div className="conversation-list" ref={setConversationListRef}>
          <div className="filters">
            <div className="filters__agent-select">
              <SearchableDropdown
                options={agentOptions}
                value={filters.assistantId}
                onChange={(id) => setFilters((prev) => ({ ...prev, assistantId: id || '' }))}
                placeholder={assistantsLoading ? 'Loading agents…' : 'Search agent…'}
              />
            </div>
            <input
              type="date"
              name="startDate"
              placeholder="Start Date"
              value={filters.startDate}
              onChange={handleFilterChange}
            />
            <input
              type="date"
              name="endDate"
              placeholder="End Date"
              value={filters.endDate}
              onChange={handleFilterChange}
            />
            <div className="checkbox-container">
              <input
                type="checkbox"
                checked={isSandboxChecked}
                onChange={handleSandboxChange}
              />
              <label>Sandbox</label>
            </div>
          </div>
          {sortedConversations.slice(0, visibleConversations).map((conversation) => (
            <ConversationPreview
              key={conversation.thread_id}
              preview={conversation}
              assistants={assistants}
              onSelect={() => handleSelectedConvo(conversation.thread_id)}
              isSelected={selectedConversation && selectedConversation.thread_id === conversation.thread_id}
            />
          ))}
          {isLoadingMore && (
            <div style={{ textAlign: 'center', padding: '10px', color: '#666' }}>
              Loading more conversations...
            </div>
          )}
        </div>
        <div className="selected-conversation-container" ref={selectedConversationRef}>
          {selectedConversation && (
            <div>
              <ConversationItem displayed={selectedConversation} onDelete={handleDeleteConversation} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
